/**
 * Pending Entry Storage System
 *
 * Manages sessionStorage-based storage for pending entries during the
 * anonymous-to-authenticated user conversion flow. When an anonymous user
 * attempts to save an entry, we store it here before redirecting to sign-up.
 * After authentication, the pending entry is retrieved and saved to the database.
 */

export interface PendingEntry {
  zoneId: string
  note: string
  timestamp: number
}

const STORAGE_KEY = "vnitrni-mapa-pending-entry"

/**
 * Check if we're running in the browser (not SSR)
 */
function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof sessionStorage !== "undefined"
}

/**
 * Save a pending entry to sessionStorage before redirecting to auth
 */
export function savePendingEntry(entry: PendingEntry): void {
  if (!isBrowser()) return

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
  } catch (error) {
    console.error("Failed to save pending entry:", error)
  }
}

/**
 * Retrieve the pending entry after authentication
 * Returns null if no pending entry exists or if parsing fails
 */
export function getPendingEntry(): PendingEntry | null {
  if (!isBrowser()) return null

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (!stored) return null

    const parsed = JSON.parse(stored) as unknown

    // Validate the parsed data has the expected shape
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "zoneId" in parsed &&
      "note" in parsed &&
      "timestamp" in parsed &&
      typeof (parsed as PendingEntry).zoneId === "string" &&
      typeof (parsed as PendingEntry).note === "string" &&
      typeof (parsed as PendingEntry).timestamp === "number"
    ) {
      return parsed as PendingEntry
    }

    // Invalid data structure, clear it
    clearPendingEntry()
    return null
  } catch (error) {
    console.error("Failed to get pending entry:", error)
    // Clear corrupted data
    clearPendingEntry()
    return null
  }
}

/**
 * Clear the pending entry after successful save or on error
 */
export function clearPendingEntry(): void {
  if (!isBrowser()) return

  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.error("Failed to clear pending entry:", error)
  }
}

/**
 * Check if a pending entry exists without retrieving it
 */
export function hasPendingEntry(): boolean {
  if (!isBrowser()) return false

  try {
    return sessionStorage.getItem(STORAGE_KEY) !== null
  } catch (error) {
    console.error("Failed to check pending entry:", error)
    return false
  }
}
